import { axiosApiClient, axiosPublicClient } from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/festivals";

class FestivalsService {
  getFestivals = async ({
    page = 1,
    search,
    limit = 12,
    includeInactive = false,
  }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/`, {
        params: {
          page,
          search: search || undefined,
          limit,
          includeInactive,
        },
      });

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festivals.");
    }
  };

  getFestivalById = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festival by id.");
    }
  };

  getFestivalsForDropdown = async () => {
    try {
      const response = await axiosPublicClient.get(`${BASE_URL}/dropdown`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching festivals for dropdown.",
      );
    }
  };

  getFeaturedFestivals = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/featured`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching featured festivals.",
      );
    }
  };

  createFestival = async (requestData) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/`, requestData);
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating festival.");
    }
  };

  editFestival = async ({ id, requestData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while editing festival.");
    }
  };

  deleteFestival = async (id) => {
    try {
      const response = await axiosApiClient.delete(`${BASE_URL}/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting festival.");
    }
  };

  toggleFestivalStatus = async (id) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}/toggle-status`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while toggling festival status.",
      );
    }
  };

  // DASHBOARD PRISTUP

  createDashboardAccess = async ({ festivalId, username, password }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/${festivalId}/dashboard-access`,
        { username, password },
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating dashboard access.");
    }
  };

  getAdminCredentials = async (festivalId) => {
    const response = await axiosApiClient.get(
      `${BASE_URL}/${festivalId}/admin-credentials`,
    );
    return response.data.data;
  };

  resetDashboardPassword = async ({ festivalId, newPassword }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/${festivalId}/dashboard-password`,
        { newPassword },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while resetting dashboard password.",
      );
    }
  };

  removeDashboardAccess = async (festivalId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/${festivalId}/dashboard-access`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while removing dashboard access.");
    }
  };

  getProductsByFestival = async ({ id, page = 1, limit = 12, search }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${id}/products`,
        {
          params: {
            page,
            limit,
            search: search || undefined,
          },
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching products by festival.",
      );
    }
  };

  // FESTIVAL DETAILS

  getFestivalDetails = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${id}/details`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festival details.");
    }
  };

  updateFestivalDetails = async ({ id, data, isFormData }) => {
    try {
      let requestConfig = {};
      let requestData = data;

      if (isFormData) {
        requestConfig = {};
        requestData = data;
      } else {
        requestData = {
          name: data.name,
          description: data.biography,
          biography: data.biography,
          city: data.city,
          founded: parseInt(data.founded) || null,

          events: Array.isArray(data.events) ? data.events : [],
          gallery: Array.isArray(data.gallery) ? data.gallery : [],

          socialLinks: data.socialLinks || {},
          stats: data.stats || {},

          isFeatured: Boolean(data.isFeatured),
          active: data.active !== false,
        };
      }

      const response = await axiosApiClient.put(
        `${BASE_URL}/${id}/details`,
        requestData,
        requestConfig,
      );

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating festival details.");
    }
  };

  getFestivalPublicDetails = async (id) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${id}/public-details`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching public festival details.",
      );
    }
  };

  getFestivalByName = async (name) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/by-name/${encodeURIComponent(name)}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festival by name.");
    }
  };

  // DASHBOARD ANALITIKA

  getFestivalAnalytics = async (festivalId) => {
    try {
      const { data } = await axiosApiClient.get(
        `${BASE_URL}/${festivalId}/analytics`,
      );
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching festival analytics.",
      );
    }
  };

  getFestivalProductAnalytics = async (festivalId, limit = 10) => {
    try {
      const { data } = await axiosApiClient.get(
        `${BASE_URL}/${festivalId}/product-analytics`,
        { params: { limit } },
      );
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching festival product analytics.",
      );
    }
  };

  getFestivalOrders = async (festivalId, params = {}) => {
    try {
      const { data } = await axiosApiClient.get(
        `${BASE_URL}/${festivalId}/orders`,
        { params },
      );
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festival orders.");
    }
  };

  getMonthlyFestivalRevenuesByPeriod = async ({
    festivalId,
    startYear,
    startMonth,
    endYear,
    endMonth,
  }) => {
    try {
      const { data } = await axiosApiClient.get(
        `${BASE_URL}/${festivalId}/revenue/monthly/period`,
        { params: { startYear, startMonth, endYear, endMonth } },
      );
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching monthly festival revenues by period.",
      );
    }
  };

  getFestivalViewStats = async (festivalId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${festivalId}/view-stats`,
      );
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching festival views.");
    }
  };
}

const festivalsService = new FestivalsService();
export default festivalsService;
